import { Box, Button, Typography, List, ListItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  tpTheme,
  TrainingpeaksVirtual,
  LibraryBooks,
  MonitorHeart,
  Strength,
  DevicesWearables,
  TpPremiumHashFull
} from "@trainingpeaks/designtokens";
import { useSubscriptionContext } from "@context/SubscriptionContext";
import { User } from "@appTypes/index";
import PremiumUpgradeComponentOptionsList from "./PremiumUpgradeComponentOptionsList";
import { useState } from "react";
import { USER_TYPE_BASIC } from "@constants/user";
import { navigateTo } from "@utils/navigate";
import { STRIPE_CHECKOUT_BASE_URL } from "@config";

interface PremiumUpgradeComponentProps {
  user: User;
}

const PremiumUpgradeComponent = ({ user }: PremiumUpgradeComponentProps) => {
  const { t } = useTranslation();
  const { subscriptions } = useSubscriptionContext();
  const [selected, setSelected] = useState<"annual" | "monthly">("annual");

  const features = [
    {
      icon: <LibraryBooks size="24" fill={tpTheme.semanticLayer.colors.primary.main} />,
      text: t("Flexible planning to fit your life"),
    },
    {
      icon: <MonitorHeart size="24" fill={tpTheme.semanticLayer.colors.primary.main} />,
      text: t("Actionable Insights to keep you on track"),
    },
    {
      icon: <Strength size="24" fill={tpTheme.semanticLayer.colors.primary.main} />,
      text: t("Strength training to avoid injury"),
    },
    {
      icon: <TrainingpeaksVirtual size="24" fill={tpTheme.semanticLayer.colors.primary.main} />,
      text: t("Ride the world inside on Virtual"),
    },
    {
      icon: <DevicesWearables size="24" fill={tpTheme.semanticLayer.colors.primary.main} />,
      text: t("Any sport, any device, any time"),
    },
  ];

  const options = [
    {
      label: t("Annual"),
      value: "annual" as const,
      originalPrice: "$19.95",
      currentPrice: "$11.25/mo",
      labelSubText: t("Billed USD $134.99/yr"),
      isMostPopular: true
    },
    {
      label: t("Monthly"),
      value: "monthly" as const,
      originalPrice: null,
      currentPrice: "$19.95/mo",
      labelSubText: null,
      isMostPopular: false
    },
  ];

  if (!user.premiumTrial && (user.userType !== USER_TYPE_BASIC || subscriptions.length > 0)) {
    return null;
  }

  const handleCheckout = () => {
    const durationParam = selected === "annual" ? "12" : "1";
    navigateTo(`${STRIPE_CHECKOUT_BASE_URL}/product/sca?duration=${durationParam}`);
  };

  return (
    <Box className="max-w-2xl mt-8 md:mt-24 mx-auto" data-testid="premium-upgrade-component">
      <TpPremiumHashFull
        size="24"
        fill={tpTheme.semanticLayer.colors.primary.main}
        fillText={tpTheme.semanticLayer.colors.premium.light}
      />
      <Typography variant="h1" className="mb-2">
        {t("Progress starts with Premium.")}
      </Typography>
      <Box className="feature-list w-full mb-4">
        <List className="flex flex-col gap-3">
          {features.map((f, idx) => (
            <ListItem key={idx} className="px-0 flex items-center gap-3">
              {f.icon}
              <Typography variant="body1">{f.text}</Typography>
            </ListItem>
          ))}
        </List>
      </Box>
      <PremiumUpgradeComponentOptionsList
        options={options}
        selected={selected}
        setSelected={setSelected}
      />
      <Box className="w-full flex justify-end">
        <Button
          variant="contained"
          color="primary"
          className="w-full mt-2 rounded-full"
          size="large"
          onClick={handleCheckout}
        >
          {t("Checkout")}
        </Button>
      </Box>
    </Box>
  );
};

export default PremiumUpgradeComponent;
